import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmModal } from '../components/ConfirmModal';
import { Toast, type ToastVariant } from '../components/Toast';
import { apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { usePageTitle } from '../components/AppShell';
import { T } from '../lib/tokens';

interface Props { user: AuthUser }
interface ToastState { message: string; variant: ToastVariant }
interface ResetResult { success: boolean; durationMs: number }

const isSuperAdmin = (user: AuthUser) =>
  user.roles.some((r) => r.role === 'super_admin' && r.schoolId === null);

const WHAT_RESETS = [
  ['🔑', 'Kata laluan + akaun ujian', '7 pengguna — admin, guru, ibubapa, moe, dpe, mora, superadmin'],
  ['🏫', 'Tahun persekolahan 2025/2026', 'SMAS Sayyidina Ali — 3 kelas (data transaksi dipadam)'],
  ['🎓', '2 rekod pelajar ujian', 'STU-001, STU-002'],
  ['📋', '1 rekod pendidik ujian', 'EMP-001'],
] as const;

const WHAT_PRESERVED = [
  '200 sekolah dalam pangkalan data',
  'Semua pengguna bukan ujian',
  'Konfigurasi sistem',
] as const;

export function DemoControlsPage({ user }: Props) {
  const { lang } = useLang();
  const E = lang === 'en';
  usePageTitle(E ? 'System Controls' : 'Kawalan Sistem');

  const [modalOpen, setModalOpen] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [toast,     setToast]     = useState<ToastState | null>(null);
  const [lastReset, setLastReset] = useState<{ at: Date; durationMs: number } | null>(null);

  const superAdmin = isSuperAdmin(user);

  async function handleReset() {
    setLoading(true);
    try {
      const result = await apiPost<ResetResult>('/api/admin/reset');
      if (!result.success) throw new Error('Pelayan melaporkan kegagalan. / Server reported failure.');
      setModalOpen(false);
      setLastReset({ at: new Date(), durationMs: result.durationMs });
      setToast({ variant: 'success', message: `Data ujian telah ditetapkan semula (${result.durationMs} ms).` });
    } catch (err) {
      setModalOpen(false);
      setToast({ variant: 'error', message: err instanceof Error ? err.message : 'Ralat tidak diketahui. / Unknown error.' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div style={{ maxWidth: '600px' }}>
        {/* Page header */}
        <div className="page-header">
          <div>
            <h1 style={{ fontFamily: T.font.serif, fontSize: '24px', fontWeight: 700, color: T.color.textPrimary, marginBottom: '4px' }}>
              {E ? 'System Controls' : 'Kawalan Sistem'}
            </h1>
            <p style={{ fontSize: '13px', color: T.color.textSecondary }}>
              {E ? 'Super Administrator access only' : 'Akses Pentadbir Super sahaja'}
            </p>
          </div>
          <span className="badge" style={{ background: '#1E2128', color: '#F7D000', fontSize: '11px' }}>
            ⚙ Super Admin
          </span>
        </div>

        {!superAdmin ? (
          <div style={{ background: T.color.dangerLight, border: `1px solid ${T.color.danger}40`, borderRadius: '10px', padding: '24px', textAlign: 'center' }}>
            <p style={{ margin: '0 0 8px', fontSize: '14px', color: T.color.textPrimary, fontWeight: 600 }}>
              {E ? 'Access Denied' : 'Akses Ditolak'}
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: T.color.textSecondary }}>
              {E ? 'This page is restricted to Super Administrators only.' : 'Halaman ini hanya untuk Pentadbir Super sahaja.'}
            </p>
          </div>
        ) : (
          <>
            {/* Last reset notice */}
            {lastReset && (
              <div style={{ background: T.color.successLight, border: `1px solid ${T.color.success}40`, borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ color: T.color.success }}>✓</span>
                <span style={{ fontSize: '13px', color: T.color.success }}>
                  {E ? 'Last reset:' : 'Set semula terakhir:'} {lastReset.at.toLocaleTimeString('ms-BN')} · {lastReset.durationMs} ms
                </span>
              </div>
            )}

            {/* Data reset section */}
            <div style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '10px', overflow: 'hidden', marginBottom: '20px' }}>
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #E5E7EB' }}>
                <h2 style={{ fontFamily: T.font.serif, fontSize: '16px', fontWeight: 700, color: T.color.textPrimary, marginBottom: '4px' }}>
                  {E ? 'Reset Test Data' : 'Tetapkan Semula Data Ujian'}
                </h2>
                <p style={{ fontSize: '13px', color: T.color.textSecondary, margin: 0 }}>
                  {E ? 'Idempotent operation — completes in under 10s. Full transaction with automatic rollback on failure.' : 'Operasi idempotent — selesai dalam bawah 10 saat. Transaksi penuh dengan rollback automatik sekiranya gagal.'}
                </p>
              </div>
              <div style={{ padding: '20px 24px' }}>
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
                    {E ? 'What resets' : 'Yang ditetapkan semula'}
                  </p>
                  {WHAT_RESETS.map(([icon, label, sub]) => (
                    <div key={label} style={{ display: 'flex', gap: '10px', marginBottom: '8px', alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '15px', lineHeight: '1.5', flexShrink: 0 }}>{icon}</span>
                      <div>
                        <span style={{ fontSize: '13px', color: T.color.textPrimary }}>{label}</span>
                        <span style={{ fontSize: '12px', color: T.color.textSecondary, marginLeft: '8px' }}>{sub}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
                    {E ? 'What is preserved' : 'Yang dipelihara'}
                  </p>
                  {WHAT_PRESERVED.map(item => (
                    <div key={item} style={{ display: 'flex', gap: '10px', marginBottom: '6px', alignItems: 'center' }}>
                      <span style={{ color: T.color.success, fontSize: '13px' }}>✓</span>
                      <span style={{ fontSize: '13px', color: T.color.textSecondary }}>{item}</span>
                    </div>
                  ))}
                </div>
                <button
                  className="btn-danger"
                  style={{ width: '100%', height: '44px', justifyContent: 'center' }}
                  onClick={() => setModalOpen(true)}
                  disabled={loading}
                >
                  {loading ? (E ? 'Resetting…' : 'Menetapkan semula…') : (E ? 'Reset Test Data' : 'Tetapkan Semula Data Ujian')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <ConfirmModal
        open={modalOpen}
        loading={loading}
        onConfirm={handleReset}
        onCancel={() => { if (!loading) setModalOpen(false); }}
      />

      {toast && (
        <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />
      )}
    </>
  );
}
